import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Divider, Form, Icon, Input } from 'antd';
import InfoBox from '../../../components/InfoBox/InfoBox';
import { getOnboardingPathLabel } from '../onboardingLinkParams';
import { ORG_ONBOARDING_INFO_COLOR } from '../onboardingConstants';
import { isPurchaseEnabled } from '../../../utils/featureGates';

const FormItem = Form.Item;

export default function PreRegistrationGate({
  form,
  linkParams,
  initialActivation,
  isSubmitting,
  activationError,
  onCreateAccount,
  subscriptionKeyPlaceholder,
}) {
  function validateActivationCredentials(_rule, _value, callback) {
    const subscriptionKey = (form.getFieldValue('subscriptionKey') || '').trim();
    const promoCode = (form.getFieldValue('promoCode') || '').trim();

    if (subscriptionKey || promoCode) {
      callback();
      return;
    }

    callback('Enter a subscription key or promo code.');
  }

  const { getFieldDecorator } = form;

  const hasFromLink = linkParams.hasActivationFromLink;
  const pathLabel = getOnboardingPathLabel(linkParams.userType);
  const hasInvitationContext = Boolean(linkParams.email || linkParams.userType);

  return (
    <>
      {isPurchaseEnabled() ? (
        <aside className="onboarding-registration-purchase-prompt" aria-label="Purchase a subscription">
          <div className="onboarding-registration-purchase-prompt-copy">
            <strong>Need a subscription?</strong>
            <span>Purchase a plan first, then return here with your subscription key to continue onboarding.</span>
          </div>
          <Link className="onboarding-registration-purchase-prompt-link" to="/purchase">
            Purchase subscription
            <Icon type="arrow-right" />
          </Link>
        </aside>
      ) : null}

      <section className="onboarding-registration-pre-gate" aria-labelledby="onboarding-registration-pre-gate-heading">
      <div className="onboarding-registration-pre-gate-header">
        <span className="onboarding-registration-panel-icon onboarding-registration-pre-gate-icon">
          <Icon type="key" />
        </span>
        <div className="onboarding-registration-pre-gate-heading" id="onboarding-registration-pre-gate-heading">
          <strong>Activation</strong>
          <small>
            {hasFromLink
              ? 'We found invitation details - review below, then continue to onboarding'
              : 'Enter your subscription key or promo code, then continue to onboarding'}
          </small>
        </div>
      </div>

      <div className="onboarding-registration-pre-gate-body">
        <div className="onboarding-registration-pre-gate-main">
          {hasInvitationContext && (
            <InfoBox
              className="onboarding-registration-link-details"
              type="fill"
              color="#237804"
              honorSectionGuidanceToggle={false}
              message={
                <div>
                  <strong>Invitation details detected</strong>
                  {linkParams.email && (
                    <p>
                      <strong>Email:</strong> {linkParams.email}
                    </p>
                  )}
                  {pathLabel && (
                    <p>
                      <strong>Registration type:</strong> {pathLabel}
                    </p>
                  )}
                  {hasFromLink && <p>Activation values from your link are prefilled below. Adjust if needed.</p>}
                </div>
              }
            />
          )}

          {!hasInvitationContext && (
            <InfoBox
              className="onboarding-registration-link-details"
              color={ORG_ONBOARDING_INFO_COLOR}
              honorSectionGuidanceToggle={false}
              message={
                <div>
                  <p>
                    <strong>Before you onboard</strong>
                  </p>
                  <p>Use one of the options below so we can link your licence to this registration.</p>
                </div>
              }
            />
          )}

          {activationError && (
            <Alert
              className="onboarding-registration-link-details"
              showIcon
              type="error"
              message="Activation invalid"
              description={activationError}
            />
          )}

          <FormItem label="Subscription Key">
            {getFieldDecorator('subscriptionKey', {
              initialValue: initialActivation.subscriptionKey,
              rules: [{ validator: validateActivationCredentials }],
            })(
              <Input
                prefix={<Icon type="key" />}
                placeholder={subscriptionKeyPlaceholder}
                disabled={isSubmitting}
                autoComplete="off"
                spellCheck={false}
              />,
            )}
          </FormItem>
          <p className="onboarding-registration-field-help">
            Enter your subscription key here to process your registration.
          </p>

          <Divider>OR</Divider>

          <FormItem label="Promo Code">
            {getFieldDecorator('promoCode', {
              initialValue: initialActivation.promoCode,
              rules: [{ validator: validateActivationCredentials }],
            })(
              <Input
                prefix={<Icon type="star" />}
                placeholder="Enter promo code"
                disabled={isSubmitting}
                autoComplete="off"
                spellCheck={false}
              />,
            )}
          </FormItem>
          <p className="onboarding-registration-field-help">Have a promo code? Enter it here to activate.</p>
        </div>

        <div className="onboarding-registration-actions onboarding-registration-actions-between onboarding-registration-actions-bar">
          <p className="onboarding-registration-step-trust">
            <Icon type="safety-certificate" /> Keys and promo codes are verified before registration opens.
          </p>
          <Button type="primary" htmlType="button" loading={isSubmitting} onClick={onCreateAccount}>
            <Icon type="check-circle" /> Verify &amp; continue
          </Button>
        </div>
      </div>
    </section>
    </>
  );
}
